import { DeleteIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Spinner,
    Text,
    VStack,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import PageCard from '../../../components/page-card';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import {
    ConversationMessageDto,
    useGetConversation,
} from '../hooks/useGetConversation';
import { useDeleteConversationMessages } from '../hooks/useDeleteConversationMessages';

export default function ConversationDetailPage() {
    const { conversationId } = useParams<{ conversationId: string }>();
    const { selectedOrganization } = useSelectedOrganization();
    const toast = useToast();

    const { data: conversation, isError, isInitialLoading } = useGetConversation(
        selectedOrganization?.id || '',
        conversationId,
    );
    // `useGetConversation` is `enabled: !!organizationId && !!conversationId`,
    // and BOTH terms can be unsatisfied on a page reached by link: the selected
    // organization is a zustand value that starts `undefined` (and stays so for
    // the session if `my-organizations` fails — run 100), and `conversationId`
    // comes from the route. React Query v4 parks a disabled query at
    // `isLoading: true`, so the old `isLoading || (!conversation && !error)`
    // branch rendered a spinner that could never resolve.
    const readState = classifyRead(isError, isInitialLoading, conversation);

    const { mutateAsync: clearMessages, isLoading: isClearing } =
        useDeleteConversationMessages();

    const {
        isOpen: isClearOpen,
        onOpen: onClearOpen,
        onClose: onClearClose,
    } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);

    const handleClearMessages = async () => {
        if (!conversationId) return;
        try {
            await clearMessages(conversationId);
            toast({
                title: translate('whatsapp_conversations.messages_cleared' as any),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClearClose();
        } catch (error: any) {
            toast({
                title: translate('whatsapp_conversations.clear_error' as any),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (readState === 'loading') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    // A never-issued read has TWO possible missing terms here and they do not
    // deserve the same answer (run 110's `leaderboardReadView` lesson: name the
    // term that is missing). No organization is a scope the navbar picker
    // fixes, so it gets the shared prompt; a missing or garbled
    // `conversationId` is an entity-id gate, and R98-1 says the honest render
    // for that is a not-found/failure card, never a prompt for something the
    // operator has already chosen.
    if (readState === 'never-issued' && !selectedOrganization?.id) {
        return <ScopePrompt gate="organization" />;
    }

    if (readState !== 'answered' || !conversation) {
        return (
            <Alert status="error">
                <AlertIcon />
                {translate('whatsapp_conversations.load_error' as any)}
            </Alert>
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            <PageCard
                showBackButton
                title={translate('whatsapp_conversations.conversation_details' as any)}
                rightHeader={
                    <Button
                        leftIcon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={onClearOpen}
                    >
                        {translate('whatsapp_conversations.clear_messages' as any)}
                    </Button>
                }
            >
                <ConversationInfo conversation={conversation} />
            </PageCard>

            <PageCard
                title={`${translate('whatsapp_conversations.messages' as any)} (${conversation.messages.length})`}
            >
                {conversation.messages.length === 0 ? (
                    <Flex px="25px" py="50px" justifyContent="center">
                        <Text color="gray.500" fontSize="lg">
                            {translate('whatsapp_conversations.no_messages' as any)}
                        </Text>
                    </Flex>
                ) : (
                    <MessageThread messages={conversation.messages} />
                )}
            </PageCard>

            <AlertDialog
                isOpen={isClearOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClearClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {translate('whatsapp_conversations.clear_messages' as any)}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('whatsapp_conversations.clear_confirm' as any)}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClearClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={handleClearMessages}
                                ml={3}
                                isLoading={isClearing}
                            >
                                {translate('whatsapp_conversations.clear_messages' as any)}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </VStack>
    );
}

function ConversationInfo({ conversation }: { conversation: { senderPhone: string; user: { id: string; firstName: string; lastName: string } | null; createdAt: string; updatedAt: string } }) {
    const textColor = useColorModeValue('secondaryGray.900', 'white');

    return (
        <HStack spacing={8} px={6} py={4} wrap="wrap">
            <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">
                    {translate('whatsapp_conversations.phone' as any)}
                </Text>
                <Text color={textColor} fontWeight="700">
                    {conversation.senderPhone}
                </Text>
            </VStack>
            <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">
                    {translate('whatsapp_conversations.user' as any)}
                </Text>
                {conversation.user ? (
                    <Text color={textColor}>
                        {conversation.user.firstName} {conversation.user.lastName}
                    </Text>
                ) : (
                    <Badge colorScheme="gray">{translate('whatsapp_conversations.unknown_user' as any)}</Badge>
                )}
            </VStack>
            <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">
                    {translate('whatsapp_conversations.last_activity' as any)}
                </Text>
                <Text color={textColor}>
                    {new Date(conversation.updatedAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    })}
                </Text>
            </VStack>
        </HStack>
    );
}

function MessageThread({ messages }: { messages: ConversationMessageDto[] }) {
    const userBg = useColorModeValue('blue.50', 'blue.900');
    const assistantBg = useColorModeValue('gray.50', 'gray.700');
    const textColor = useColorModeValue('secondaryGray.900', 'white');

    return (
        <VStack spacing={3} px={6} py={4} align="stretch">
            {messages.map((msg) => {
                const isUser = msg.role === 'user';
                return (
                    <Flex
                        key={msg.id}
                        justify={isUser ? 'flex-end' : 'flex-start'}
                    >
                        <Box
                            maxW="70%"
                            bg={isUser ? userBg : assistantBg}
                            px={4}
                            py={3}
                            borderRadius="lg"
                            borderTopRightRadius={isUser ? '0' : 'lg'}
                            borderTopLeftRadius={isUser ? 'lg' : '0'}
                        >
                            <Text
                                fontSize="xs"
                                color="gray.400"
                                fontWeight="bold"
                                mb={1}
                            >
                                {isUser ? 'User' : 'Assistant'}
                            </Text>
                            <Text
                                color={textColor}
                                fontSize="sm"
                                whiteSpace="pre-wrap"
                            >
                                {msg.content}
                            </Text>
                            <Text fontSize="xs" color="gray.400" mt={1} textAlign="right">
                                {new Date(msg.createdAt).toLocaleTimeString('en-GB', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </Text>
                        </Box>
                    </Flex>
                );
            })}
        </VStack>
    );
}
